import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Select,
  AutoComplete,
  Card,
  Button,
  Tooltip,
  Spin,
  Modal,
  Empty,
  Row,
  Col,
  Divider,
  Typography,
  message,
  Checkbox,
  Space,
  Switch,
  Tag,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  DownloadOutlined,
  CalendarOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  TeamOutlined,
  RightOutlined,
  AppstoreOutlined,
  BorderOuterOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Option } = Select;
const { Title, Text } = Typography;

const STATUS_CFG = {
  Planned: { bg: "#F1EFE8", color: "#5F5E5A", dot: "#888780" },
  Loading: { bg: "#FAEEDA", color: "#854F0B", dot: "#EF9F27" },
  Dispatched: { bg: "#E6F1FB", color: "#185FA5", dot: "#378ADD" },
  "In Transit": { bg: "#EEEDFE", color: "#534AB7", dot: "#7F77DD" },
  Delivered: { bg: "#EAF3DE", color: "#3B6D11", dot: "#639922" },
};

const PROD_STATUS_CFG = {
  Complete: { bg: "#EAF3DE", color: "#3B6D11" },
  "In Progress": { bg: "#FAEEDA", color: "#854F0B" },
  Pending: { bg: "#F1EFE8", color: "#5F5E5A" },
};

const DELIVERY_STATUS_CFG = {
  "Ready for Dispatch": { bg: "#E6F1FB", color: "#185FA5" },
  Dispatched: { bg: "#EEEDFE", color: "#534AB7" },
  "In Transit": { bg: "#EEEDFE", color: "#534AB7" },
  Delivered: { bg: "#EAF3DE", color: "#3B6D11" },
  scheduled: { bg: "#F0F6FF", color: "#185FA5" },
  "awaiting dispatch": { bg: "#FFF2DD", color: "#A15E00" },
};

const ROUTE_ORDER_STATUS_CFG = {
  Assigned: { bg: "#F0F6FF", color: "#185FA5" },
  Loaded: { bg: "#FAEEDA", color: "#854F0B" },
  "Out For Delivery": { bg: "#EEEDFE", color: "#534AB7" },
  Delivered: { bg: "#EAF3DE", color: "#3B6D11" },
  Failed: { bg: "#FCEBEB", color: "#A32D2D" },
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const safe = (v, fb = "—") => (v === null || v === undefined || String(v).trim() === "" ? fb : String(v));
const normalize = (v) => String(v ?? "").trim().toLowerCase();

const formatDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? String(s)
    : new Intl.DateTimeFormat("en-IE", { day: "2-digit", month: "short", year: "numeric" }).format(d);
};

const formatDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? String(s)
    : new Intl.DateTimeFormat("en-IE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
};

const formatTimeLabel = (time) => {
  if (!time) return "";
  const [hh, mm] = String(time).split(":");
  const h = Number(hh);
  if (Number.isNaN(h)) return String(time);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${mm || "00"} ${suffix}`;
};

const buildSlotLabel = (slot) => {
  if (!slot) return "—";
  if (slot.label) return slot.label;
  const day = slot.day || "";
  const start = slot.startTime || "";
  const end = slot.endTime || "";
  if (day || start || end) return `${day}${day && start ? " • " : ""}${start}${start && end ? " – " : ""}${end}`;
  return "—";
};

const StatusBadge = ({ status, small = false }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.Planned;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: cfg.bg,
        color: cfg.color,
        fontSize: small ? 11 : 12,
        fontWeight: 700,
        padding: small ? "2px 8px" : "4px 10px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        border: `1px solid ${cfg.color}18`,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const Pill = ({ text, cfgMap }) => {
  const c = (cfgMap && cfgMap[text]) || { bg: "#F1EFE8", color: "#5F5E5A" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: c.bg,
        color: c.color,
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        border: `1px solid ${c.color}14`,
      }}
    >
      {text || "—"}
    </span>
  );
};

function RouteModal({ visible, mode, slots, drivers, vehicles, initialValues, onClose, onSave }) {
  const blank = {
    name: "",
    slotId: "",
    driver: "",
    vehicle: "",
    capacity: 20,
    scheduledDate: "",
    notes: "",
    status: "Planned",
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (visible) {
      setForm({
        name: initialValues?.name || "",
        slotId: initialValues?.slotId ? String(initialValues.slotId) : "",
        driver: initialValues?.driver || "",
        vehicle: initialValues?.vehicle || "",
        capacity: Number(initialValues?.capacity || 20),
        scheduledDate: initialValues?.scheduledDate || "",
        notes: initialValues?.notes || "",
        status: initialValues?.status || "Planned",
      });
    }
  }, [visible, initialValues]);

  const inp = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #DDE3EE",
    borderRadius: 12,
    fontSize: 13,
    background: "#FAFAFE",
    boxSizing: "border-box",
    outline: "none",
    transition: "all 0.2s ease",
  };

  const save = () => {
    if (!form.name.trim()) {
      message.error("Route name is required");
      return;
    }
    if (!form.slotId) {
      message.error("Please select a delivery slot");
      return;
    }
    onSave(form);
  };

  const driverOptions = (drivers || []).map((d) => ({ value: d.name }));
  const vehicleOptions = (vehicles || []).map((v) => ({ value: v.name }));

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
      centered
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: "linear-gradient(135deg,#0f1e38,#1a2b49)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <TruckOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
              {mode === "edit" ? "Edit Route" : "Create New Route"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Configure route details, slot, driver, and vehicle.
            </div>
          </div>
        </div>
      }
      styles={{ body: { paddingTop: 10 } }}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div
          style={{
            border: "1px solid #E2E8F0",
            borderRadius: 16,
            padding: 16,
            background: "linear-gradient(180deg,#ffffff 0%, #fbfdff 100%)",
          }}
        >
          <div style={{ display: "grid", gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                Route Name
              </label>
              <input
                style={inp}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Dublin West Route"
              />
            </div>

            <Row gutter={12}>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                  Delivery Slot
                </label>
                <Select
                  value={form.slotId || undefined}
                  onChange={(v) => setForm((p) => ({ ...p, slotId: String(v) }))}
                  placeholder="Select slot"
                  style={{ width: "100%" }}
                  notFoundContent="No active slots found"
                  showSearch
                  optionFilterProp="children"
                >
                  {slots.map((slot) => (
                    <Option key={slot.id} value={String(slot.id)}>
                      {buildSlotLabel(slot)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                  Scheduled Date
                </label>
                <input
                  type="date"
                  style={inp}
                  value={form.scheduledDate || ""}
                  onChange={(e) => setForm((p) => ({ ...p, scheduledDate: e.target.value }))}
                />
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                  Driver
                </label>
                <AutoComplete
                  value={form.driver || ""}
                  options={driverOptions}
                  onChange={(value) => setForm((p) => ({ ...p, driver: value }))}
                  placeholder="Type or select driver"
                  style={{ width: "100%" }}
                  filterOption={(inputValue, option) =>
                    String(option?.value || "").toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Col>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                  Vehicle
                </label>
                <AutoComplete
                  value={form.vehicle || ""}
                  options={vehicleOptions}
                  onChange={(value) => setForm((p) => ({ ...p, vehicle: value }))}
                  placeholder="Type or select vehicle"
                  style={{ width: "100%" }}
                  filterOption={(inputValue, option) =>
                    String(option?.value || "").toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                  Capacity
                </label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  style={inp}
                  value={form.capacity}
                  onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))}
                />
              </Col>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                  Status
                </label>
                <Select
                  value={form.status}
                  onChange={(v) => setForm((p) => ({ ...p, status: v }))}
                  style={{ width: "100%" }}
                >
                  {Object.keys(STATUS_CFG).map((s) => (
                    <Option key={s} value={s}>
                      {s}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: "#475569", display: "block", marginBottom: 6 }}>
                Notes
              </label>
              <textarea
                rows={4}
                style={{ ...inp, resize: "vertical" }}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional route notes..."
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button onClick={onClose} style={{ minWidth: 120, height: 42 }}>
            Cancel
          </Button>
          <Button type="primary" onClick={save} style={{ minWidth: 160, height: 42 }}>
            {mode === "edit" ? "Save Changes" : "Create Route"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AssignModal({ visible, routes, orders, selectedRouteId, onClose, onConfirm }) {
  const [routeId, setRouteId] = useState("");
  const [selected, setSelected] = useState([]);

  const scheduledOrders = useMemo(() => (orders || []).filter((o) => normalize(o.deliveryStatus) === "scheduled"), [orders]);

  useEffect(() => {
    if (visible) {
      setRouteId(selectedRouteId ? String(selectedRouteId) : routes[0] ? String(routes[0].id) : "");
      setSelected([]);
    }
  }, [visible, selectedRouteId, routes]);

  const toggle = (id) => {
    const sid = String(id);
    setSelected((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={780}
      destroyOnClose
      centered
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: "linear-gradient(135deg,#d4af37,#f0c93a)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1a2b49",
            }}
          >
            <TeamOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>Assign Orders To Route</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>Only scheduled orders are shown here.</div>
          </div>
        </div>
      }
      styles={{ body: { paddingTop: 10 } }}
    >
      <Row gutter={16}>
        <Col span={8}>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 16, padding: 16, background: "#fff" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 }}>Route</div>
            <Select
              value={routeId || undefined}
              onChange={(v) => setRouteId(String(v))}
              style={{ width: "100%" }}
              placeholder="Select route"
              showSearch
              optionFilterProp="children"
            >
              {routes.map((r) => (
                <Option key={r.id} value={String(r.id)}>
                  {safe(r.name)}
                </Option>
              ))}
            </Select>

            <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
              <div style={{ padding: 12, borderRadius: 14, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>Scheduled Orders</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>{scheduledOrders.length}</div>
              </div>
              <div style={{ padding: 12, borderRadius: 14, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                <div style={{ fontSize: 12, color: "#92400e" }}>Selected</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#92400e" }}>{selected.length}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col span={16}>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 16, overflow: "hidden", background: "#fff" }}>
            <div style={{ padding: 16, borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>Scheduled Orders</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Pick one or more orders to assign to the selected route.</div>
              </div>
              <Tag color="blue">{scheduledOrders.length} available</Tag>
            </div>

            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {scheduledOrders.length === 0 ? (
                <div style={{ padding: 28, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                  No scheduled orders
                </div>
              ) : (
                scheduledOrders.map((o) => {
  const productName =
    o.product?.product_name ||
    o.product?.name ||
    o.product_name ||
    o.productName ||
    null;

  const customerName =
    o.user?.full_name ||
    o.user?.name ||
    o.user?.email ||
    o.customer ||
    o.customerName ||
    null;

  const isSelected = selected.includes(String(o.id));

  return (
    <label
      key={o.id}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        cursor: "pointer",
        borderBottom: "1px solid #F1F5F9",
        background: isSelected ? "#F0F6FF" : "transparent",
        transition: "background 0.2s ease",
      }}
    >
      <Checkbox checked={isSelected} onChange={() => toggle(o.id)} />

      <div style={{ minWidth: 0, flex: 1 }}>
        {/* Order ID + badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 800, color: "#0f172a", fontSize: 13, fontFamily: "monospace" }}>
            {safe(o.orderId || o.referenceNumber || `#${o.id}`)}
          </span>
          <Tag style={{ margin: 0 }} color="geekblue">Scheduled</Tag>
        </div>

        {/* Product name */}
        <div style={{ fontSize: 12, color: "#334155", fontWeight: 600, marginTop: 3 }}>
          {productName
            ? productName
            : <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No product name</span>}
        </div>

        {/* Customer name */}
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
          {customerName ? (
            <>
              <UserOutlined style={{ marginRight: 4, fontSize: 11 }} />
              {customerName}
            </>
          ) : (
            <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No customer</span>
          )}
        </div>
      </div>
    </label>
  );
})
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}>
            <Button onClick={onClose} style={{ minWidth: 120, height: 42 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              style={{ minWidth: 180, height: 42 }}
              disabled={!routeId || selected.length === 0}
              onClick={() => onConfirm(routeId, selected)}
            >
              Assign {selected.length > 0 ? `(${selected.length})` : ""} Orders
            </Button>
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

function ListManagerModal({ visible, title, itemLabel, items, onClose, onAdd, onUpdate, onDelete }) {
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (visible) {
      setName("");
      setEditing(null);
    }
  }, [visible]);

  const startEdit = (item) => {
    setEditing(item);
    setName(item?.name || "");
  };

  const submit = async () => {
    const value = String(name || "").trim();
    if (!value) {
      message.error(`${itemLabel} name is required`);
      return;
    }
    if (editing) {
      await onUpdate(editing.id, value);
      setEditing(null);
    } else {
      await onAdd(value);
    }
    setName("");
  };

  return (
    <Modal open={visible} onCancel={onClose} footer={null} width={640} destroyOnClose centered title={title}>
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`Enter ${itemLabel.toLowerCase()} name`} />
          <Button type="primary" onClick={submit}>
            {editing ? "Update" : "Add"}
          </Button>
          {editing && (
            <Button
              onClick={() => {
                setEditing(null);
                setName("");
              }}
            >
              Cancel Edit
            </Button>
          )}
        </div>

        <div style={{ border: "1px solid #E2E8F0", borderRadius: 16, overflow: "hidden", background: "#fff" }}>
          {(items || []).length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>No {itemLabel.toLowerCase()}s yet</div>
          ) : (
            (items || []).map((item) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  borderBottom: "1px solid #F1F5F9",
                }}
              >
                <div>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>{item.name}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>Custom {itemLabel.toLowerCase()} entry</div>
                </div>
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(item)} />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(item.id)} />
                </Space>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

function SlotManagerModal({ visible, slots, onClose, onSave, onDelete }) {
  const blank = { day: "Monday", startTime: "09:00", endTime: "14:00", label: "", isActive: true };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (visible) {
      setForm(blank);
      setEditingId(null);
    }
  }, [visible]);

  const startEdit = (slot) => {
    setEditingId(slot.id);
    setForm({
      day: slot.day || "Monday",
      startTime: slot.startTime || "09:00",
      endTime: slot.endTime || "14:00",
      label: slot.label || "",
      isActive: Number(slot.isActive) === 1,
    });
  };

  const submit = async () => {
    if (!form.day || !form.startTime || !form.endTime) {
      message.error("Day, start time, and end time are required");
      return;
    }
    const label = String(form.label || "").trim() || `${form.day} • ${formatTimeLabel(form.startTime)} – ${formatTimeLabel(form.endTime)}`;
    await onSave(editingId, { ...form, label, isActive: form.isActive ? 1 : 0 });
    setForm(blank);
    setEditingId(null);
  };

  return (
    <Modal open={visible} onCancel={onClose} footer={null} width={920} destroyOnClose centered title={<span style={{ fontWeight: 900, fontSize: 16 }}>Manage Delivery Slots</span>}>
      <Row gutter={16}>
        <Col span={10}>
          <Card size="small" style={{ borderRadius: 16 }}>
            <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 4 }}>{editingId ? "Edit Slot" : "Add Slot"}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Update the day-based schedule shown on routes.</div>
            <div style={{ display: "grid", gap: 10 }}>
              <Select value={form.day} onChange={(v) => setForm((p) => ({ ...p, day: v }))} style={{ width: "100%" }}>
                {DAYS.map((d) => (
                  <Option key={d} value={d}>
                    {d}
                  </Option>
                ))}
              </Select>
              <Input type="time" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} />
              <Input type="time" value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} />
              <Input value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} placeholder="Label" />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0" }}>
                <span style={{ fontWeight: 600 }}>Active</span>
                <Switch checked={form.isActive} onChange={(checked) => setForm((p) => ({ ...p, isActive: checked }))} />
              </div>
              <Space>
                <Button type="primary" onClick={submit}>{editingId ? "Update Slot" : "Add Slot"}</Button>
                {editingId && (
                  <Button onClick={() => { setEditingId(null); setForm(blank); }}>
                    Cancel
                  </Button>
                )}
              </Space>
            </div>
          </Card>
        </Col>
        <Col span={14}>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 16, overflow: "hidden", background: "#fff" }}>
            <div style={{ padding: 16, borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 900, color: "#0f172a" }}>Existing Slots</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Edit the day, start time, end time, and label.</div>
              </div>
              <Tag color="blue">{(slots || []).length} slots</Tag>
            </div>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {(slots || []).length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>No slots found</div>
              ) : (
                (slots || []).map((slot) => (
                  <div key={slot.id} style={{ padding: 14, borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>{slot.label || buildSlotLabel(slot)}</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                        {slot.day} • {slot.startTime} – {slot.endTime} • {Number(slot.isActive) === 1 ? "Active" : "Inactive"}
                      </div>
                    </div>
                    <Space>
                      <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(slot)} />
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(slot.id)} />
                    </Space>
                  </div>
                ))
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

export default function AdminDeliveryRoutes() {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [routes, setRoutes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [unassignedOrders, setUnassigned] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [routeDetail, setRouteDetail] = useState(null);

  const [statusFilter, setStatusFilter] = useState("All Routes");
  const [search, setSearch] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const [showSlotsManager, setShowSlotsManager] = useState(false);
  const [showDriversManager, setShowDriversManager] = useState(false);
  const [showVehiclesManager, setShowVehiclesManager] = useState(false);

  const api = {
    get: (url, params) => axiosInstance.get(url, { params }).then((r) => r.data),
    post: (url, body) => axiosInstance.post(url, body).then((r) => r.data),
    patch: (url, body) => axiosInstance.patch(url, body).then((r) => r.data),
    delete: (url) => axiosInstance.delete(url).then((r) => r.data),
  };

  const loadSlots = useCallback(async () => {
    try {
      const data = await api.get("/delivery-routes/slots");
      if (data?.success) setSlots(data.slots || []);
    } catch {
      message.error("Failed to load delivery slots");
    }
  }, []);

  const loadDrivers = useCallback(async () => {
    try {
      const data = await api.get("/delivery-routes/drivers");
      if (data?.success) setDrivers(data.drivers || []);
    } catch {
      setDrivers([]);
    }
  }, []);

  const loadVehicles = useCallback(async () => {
    try {
      const data = await api.get("/delivery-routes/vehicles");
      if (data?.success) setVehicles(data.vehicles || []);
    } catch {
      setVehicles([]);
    }
  }, []);

  const loadUnassigned = useCallback(async () => {
    try {
      const data = await api.get("/delivery-routes/orders/unassigned");
      if (data?.success) setUnassigned(data.orders || []);
    } catch {
      message.error("Failed to load unassigned orders");
    }
  }, []);

  const loadRoutes = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "All Routes") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();

      const data = await api.get("/delivery-routes", params);
      if (data?.success) {
        const list = data.routes || [];
        setRoutes(list);
        if (list.length > 0) {
          const stillThere = list.some((r) => Number(r.id) === Number(selectedRouteId));
          if (!stillThere) setSelectedRouteId(list[0].id);
        } else {
          setSelectedRouteId(null);
          setRouteDetail(null);
        }
      }
    } catch {
      message.error("Failed to load routes");
    }
  }, [statusFilter, search, selectedRouteId]);

  const loadRouteDetail = useCallback(async (id) => {
    if (!id) {
      setRouteDetail(null);
      return;
    }
    try {
      const data = await api.get(`/delivery-routes/${id}`);
      if (data?.success) setRouteDetail(data.route || null);
    } catch {
      message.error("Failed to load route details");
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadSlots(), loadDrivers(), loadVehicles(), loadUnassigned(), loadRoutes()]);
    } finally {
      setLoading(false);
    }
  }, [loadSlots, loadDrivers, loadVehicles, loadUnassigned, loadRoutes]);

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadRoutes(), 300);
    return () => clearTimeout(t);
  }, [statusFilter, search, loadRoutes]);

  useEffect(() => {
    if (selectedRouteId) loadRouteDetail(selectedRouteId);
  }, [selectedRouteId, loadRouteDetail]);

  const stats = useMemo(
    () => ({
      total: routes.length,
      planned: routes.filter((r) => r.status === "Planned").length,
      active: routes.filter((r) => ["Loading", "Dispatched", "In Transit"].includes(r.status)).length,
      delivered: routes.filter((r) => r.status === "Delivered").length,
      assigned: routes.reduce((s, r) => s + Number(r.ordersAssigned || 0), 0),
    }),
    [routes]
  );

  const routeOrders = useMemo(() => {
    const raw = routeDetail?.routeOrders || [];
    return raw.map((ro) => {
      const order = ro.order || {};
      const user = order.user || {};
      const product = order.product || {};
      return {
        id: ro.id,
        routeOrderStatus: ro.status || "Assigned",
        loadedAt: ro.loadedAt || null,
        deliveredAt: ro.deliveredAt || null,
        orderId: order.orderId || order.referenceNumber || order.id,
        customer: user.full_name || user.name || user.email || "—",
        productName: product.product_name || order.product_name || "—",
        qty: order.quantity || 1,
        prodStatus: order.productionStatus || "Pending",
        deliveryStatus: order.deliveryStatus || "scheduled",
      };
    });
  }, [routeDetail]);

  const createRoute = async (form) => {
    try {
      const data = await api.post("/delivery-routes", {
        name: form.name.trim(),
        slotId: Number(form.slotId),
        driver: form.driver || null,
        vehicle: form.vehicle || null,
        capacity: Number(form.capacity) || 20,
        scheduledDate: form.scheduledDate || null,
        notes: form.notes || null,
      });
      if (data?.success) {
        message.success("Route created successfully");
        setShowCreate(false);
        await refreshAll();
      } else {
        message.error(data?.message || "Failed to create route");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to create route");
    }
  };

  const updateRoute = async (form) => {
    try {
      const data = await api.patch(`/delivery-routes/${editRoute.id}`, {
        name: form.name.trim(),
        slotId: Number(form.slotId),
        driver: form.driver || null,
        vehicle: form.vehicle || null,
        capacity: Number(form.capacity) || 20,
        scheduledDate: form.scheduledDate || null,
        notes: form.notes || null,
        status: form.status,
      });
      if (data?.success) {
        message.success("Route updated");
        setEditRoute(null);
        await refreshAll();
      } else {
        message.error(data?.message || "Failed to update route");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update route");
    }
  };

  const assignOrders = async (routeId, orderIds) => {
    try {
      const data = await api.post(`/delivery-routes/${routeId}/assign`, { orderIds });
      if (data?.success) {
        message.success(`${orderIds.length} order(s) assigned`);
        setShowAssign(false);
        await refreshAll();
        setSelectedRouteId(Number(routeId));
      } else {
        message.error(data?.message || "Assignment failed");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Assignment failed");
    }
  };

  const handleRouteAction = async (action) => {
    if (!selectedRouteId) return;
    setActionLoading(true);
    try {
      const data = await api.patch(`/delivery-routes/${selectedRouteId}/status`, { action });
      if (data?.success) {
        message.success(
          action === "loading"
            ? "Route marked as Loading"
            : action === "dispatch"
            ? "Route dispatched"
            : "Route completed"
        );
        await refreshAll();
        await loadRouteDetail(selectedRouteId);
      } else {
        message.error(data?.message || "Status update failed");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Status update failed");
    } finally {
      setActionLoading(false);
    }
  };

  const markLoaded = async (routeOrderId) => {
    try {
      const data = await api.patch(`/delivery-routes/route-orders/${routeOrderId}/mark-loaded`);
      if (data?.success) {
        message.success("Order marked as loaded");
        await loadRouteDetail(selectedRouteId);
        await loadRoutes();
      } else {
        message.error(data?.message || "Failed");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to mark loaded");
    }
  };

  const deleteRoute = (routeId) => {
    Modal.confirm({
      title: "Delete this route?",
      content: "All assigned orders will be unlinked.",
      okText: "Delete",
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          const data = await api.delete(`/delivery-routes/${routeId}`);
          if (data?.success) {
            message.success("Route deleted");
            await refreshAll();
          } else {
            message.error(data?.message || "Failed to delete");
          }
        } catch (err) {
          message.error(err?.response?.data?.message || "Failed to delete");
        }
      },
    });
  };

  const deleteSlot = async (id) => {
    try {
      const data = await api.delete(`/delivery-routes/slots/${id}`);
      if (data?.success) {
        message.success("Slot deleted");
        await loadSlots();
      } else {
        message.error(data?.message || "Failed to delete slot");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete slot");
    }
  };

  const saveSlot = async (editingId, payload) => {
    try {
      const endpoint = editingId ? `/delivery-routes/slots/${editingId}` : "/delivery-routes/slots";
      const method = editingId ? "patch" : "post";
      const data = await api[method](endpoint, payload);
      if (data?.success) {
        message.success(editingId ? "Slot updated" : "Slot created");
        await loadSlots();
      } else {
        message.error(data?.message || "Failed to save slot");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save slot");
    }
  };

  const saveDriver = async (name) => {
    try {
      const data = await api.post("/delivery-routes/drivers", { name });
      if (data?.success) {
        message.success("Driver added");
        await loadDrivers();
      } else {
        message.error(data?.message || "Failed to save driver");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save driver");
    }
  };

  const updateDriver = async (id, name) => {
    try {
      const data = await api.patch(`/delivery-routes/drivers/${id}`, { name });
      if (data?.success) {
        message.success("Driver updated");
        await loadDrivers();
      } else {
        message.error(data?.message || "Failed to update driver");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update driver");
    }
  };

  const deleteDriver = async (id) => {
    try {
      const data = await api.delete(`/delivery-routes/drivers/${id}`);
      if (data?.success) {
        message.success("Driver deleted");
        await loadDrivers();
      } else {
        message.error(data?.message || "Failed to delete driver");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete driver");
    }
  };

  const saveVehicle = async (name) => {
    try {
      const data = await api.post("/delivery-routes/vehicles", { name });
      if (data?.success) {
        message.success("Vehicle added");
        await loadVehicles();
      } else {
        message.error(data?.message || "Failed to save vehicle");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save vehicle");
    }
  };

  const updateVehicle = async (id, name) => {
    try {
      const data = await api.patch(`/delivery-routes/vehicles/${id}`, { name });
      if (data?.success) {
        message.success("Vehicle updated");
        await loadVehicles();
      } else {
        message.error(data?.message || "Failed to update vehicle");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update vehicle");
    }
  };

  const deleteVehicle = async (id) => {
    try {
      const data = await api.delete(`/delivery-routes/vehicles/${id}`);
      if (data?.success) {
        message.success("Vehicle deleted");
        await loadVehicles();
      } else {
        message.error(data?.message || "Failed to delete vehicle");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete vehicle");
    }
  };

  const downloadCSV = () => {
    if (!routeDetail) return;
    const rows = [
      ["Route", routeDetail.name || ""],
      ["Status", routeDetail.status || ""],
      ["Driver", routeDetail.driver || ""],
      ["Vehicle", routeDetail.vehicle || ""],
      ["Date", routeDetail.scheduledDate || ""],
      ["Slot", routeDetail?.slot?.label || buildSlotLabel(routeDetail?.slot)],
      [],
      ["Order ID", "Customer", "Product", "Qty", "Route Status", "Delivery Status"],
      ...routeOrders.map((o) => [
        o.orderId || "",
        o.customer || "",
        o.productName || "",
        String(o.qty || 1),
        o.routeOrderStatus || "",
        o.deliveryStatus || "",
      ]),
    ];
   const csv = rows
  .map((r) =>
    r.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(",")
  )
  .join("\n");

const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
const url = URL.createObjectURL(blob);
const a = document.createElement("a");
a.href = url;
a.download = `route-${routeDetail?.name || "sheet"}.csv`;

document.body.appendChild(a);
a.click();
document.body.removeChild(a);

URL.revokeObjectURL(url);

message.success("Route sheet downloaded");
};

  const selectedRoute = routes.find((r) => Number(r.id) === Number(selectedRouteId)) || null;

  const routeColumns = [
    {
      title: "Route Name",
      key: "name",
      render: (_, r) => <span style={{ fontWeight: 800, color: "#0f172a" }}>{safe(r.name)}</span>,
    },
    {
      title: "Slot",
      key: "slot",
      width: 220,
      render: (_, r) => <span style={{ fontSize: 12, color: "#334155" }}>{safe(r.slotLabel || r.slot)}</span>,
    },
    {
      title: "Date",
      key: "date",
      width: 130,
      render: (_, r) => <span style={{ fontSize: 12, color: "#334155" }}>{formatDate(r.scheduledDate)}</span>,
    },
    {
      title: "Driver",
      key: "driver",
      width: 140,
      render: (_, r) => safe(r.driver, "No Driver"),
    },
    {
      title: "Vehicle",
      key: "vehicle",
      width: 140,
      render: (_, r) => safe(r.vehicle),
    },
    {
      title: "Orders",
      key: "orders",
      width: 110,
      render: (_, r) => (
        <span>
          <b style={{ color: "#378ADD" }}>{Number(r.ordersAssigned || 0)}</b>
          <span style={{ color: "#94a3b8" }}> / {Number(r.capacity || 0)}</span>
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: (_, r) => <StatusBadge status={r.status || "Planned"} small />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, r) => (
        <Space>
          <Tooltip title="View details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setSelectedRouteId(r.id)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditRoute(r)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteRoute(r.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const orderColumns = [
    {
      title: "Order ID",
      key: "orderId",
      width: 130,
      render: (_, r) => (
        <span style={{ fontWeight: 800, color: "#378ADD", fontFamily: "monospace", fontSize: 12 }}>
          {safe(r.orderId)}
        </span>
      ),
    },
    { title: "Customer", dataIndex: "customer", key: "customer", width: 170 },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: 210,
      render: (t) => <span style={{ fontSize: 12 }}>{safe(t)}</span>,
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 60,
      render: (v) => <b>{v}</b>,
    },
    {
      title: "Prod. Status",
      key: "prodStatus",
      width: 130,
      render: (_, r) => <Pill text={r.prodStatus} cfgMap={PROD_STATUS_CFG} />,
    },
    {
      title: "Delivery Status",
      key: "deliveryStatus",
      width: 160,
      render: (_, r) => <Pill text={r.deliveryStatus} cfgMap={DELIVERY_STATUS_CFG} />,
    },
    {
      title: "Route Status",
      key: "routeOrderStatus",
      width: 140,
      render: (_, r) => <Pill text={r.routeOrderStatus} cfgMap={ROUTE_ORDER_STATUS_CFG} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, r) => (
        <Button size="small" icon={<CheckCircleOutlined />} onClick={() => markLoaded(r.id)}>
          Mark Loaded
        </Button>
      ),
    },
  ];

  const heroMetric = (label, value, icon, accent) => (
    <Card
      bordered={false}
      style={{
        borderRadius: 18,
        boxShadow: "0 8px 28px rgba(15, 23, 42, 0.06)",
        border: "1px solid rgba(226,232,240,0.9)",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: 18 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>{value}</div>
        </div>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: accent,
            color: "#fff",
            boxShadow: `0 10px 18px ${accent}33`,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );

  return (
    <Spin spinning={loading} size="large">
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top left, rgba(212,175,55,0.10), transparent 22%), radial-gradient(circle at top right, rgba(24,95,165,0.10), transparent 24%), linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%)",
          padding: 24,
          fontFamily: "'Segoe UI', system-ui, sans-serif",
        }}
      >
        <div style={{ maxWidth: 1440, margin: "0 auto" }}>
          <Card
            bordered={false}
            style={{
              borderRadius: 24,
              marginBottom: 18,
              overflow: "hidden",
              border: "1px solid rgba(226,232,240,0.85)",
              boxShadow: "0 18px 50px rgba(15,23,42,0.08)",
              background: "linear-gradient(135deg,#0f1e38 0%,#172f52 48%,#1a2b49 100%)",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div
              style={{
                padding: 24,
                color: "#fff",
                display: "flex",
                flexWrap: "wrap",
                gap: 18,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div
                  style={{
                    width: 58,
                    height: 58,
                    borderRadius: 18,
                    background: "linear-gradient(135deg,#d4af37,#f0c93a)",
                    color: "#1a2b49",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 14px 30px rgba(212,175,55,0.25)",
                    flexShrink: 0,
                  }}
                >
                  <TruckOutlined style={{ fontSize: 24 }} />
                </div>
                <div>
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                    <Title level={3} style={{ margin: 0, color: "#fff" }}>
                      Delivery Routes Management
                    </Title>
                    <Tag color="gold" style={{ borderRadius: 999, margin: 0 }}>
                      Modern route operations
                    </Tag>
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.78)", marginTop: 6, fontSize: 13 }}>
                    Schedule routes, assign scheduled orders, and manage daily delivery slots, drivers, and vehicles.
                  </div>
                </div>
              </div>

              <Space wrap>
                <Button icon={<ReloadOutlined />} onClick={refreshAll} size="middle" style={{ borderRadius: 12, height: 42 }}>
                  Refresh
                </Button>
                <Button icon={<CalendarOutlined />} onClick={() => setShowSlotsManager(true)} size="middle" style={{ borderRadius: 12, height: 42 }}>
                  Manage Slots
                </Button>
                <Button icon={<UserOutlined />} onClick={() => setShowDriversManager(true)} size="middle" style={{ borderRadius: 12, height: 42 }}>
                  Manage Drivers
                </Button>
                <Button icon={<TruckOutlined />} onClick={() => setShowVehiclesManager(true)} size="middle" style={{ borderRadius: 12, height: 42 }}>
                  Manage Vehicles
                </Button>
                <Button
                  icon={<TeamOutlined />}
                  onClick={() => {
                    loadUnassigned();
                    setShowAssign(true);
                  }}
                  size="middle"
                  style={{ borderRadius: 12, height: 42 }}
                >
                  Assign Orders
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)} size="middle" style={{ borderRadius: 12, height: 42 }}>
                  Create Route
                </Button>
              </Space>
            </div>
          </Card>

          <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
            <Col xs={12} sm={8} xl={4} style={{ flex: 1 }}>
              {heroMetric("Total Routes", stats.total, <BorderOuterOutlined />, "#378ADD")}
            </Col>
            <Col xs={12} sm={8} xl={4} style={{ flex: 1 }}>
              {heroMetric("Planned", stats.planned, <FileTextOutlined />, "#888780")}
            </Col>
            <Col xs={12} sm={8} xl={4} style={{ flex: 1 }}>
              {heroMetric("Active", stats.active, <ClockCircleOutlined />, "#EF9F27")}
            </Col>
            <Col xs={12} sm={8} xl={4} style={{ flex: 1 }}>
              {heroMetric("Delivered", stats.delivered, <CheckCircleOutlined />, "#639922")}
            </Col>
            <Col xs={12} sm={8} xl={4} style={{ flex: 1 }}>
              {heroMetric("Orders Assigned", stats.assigned, <TeamOutlined />, "#7F77DD")}
            </Col>
          </Row>

          <Card
            bordered={false}
            style={{
              borderRadius: 22,
              marginBottom: 18,
              boxShadow: "0 10px 30px rgba(15,23,42,0.07)",
              border: "1px solid rgba(226,232,240,0.9)",
            }}
            bodyStyle={{ padding: 18 }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 15, color: "#0f172a" }}>Routes overview</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Search and filter the route list, then open a route to inspect its orders.</div>
              </div>
              <Space wrap>
                <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 170 }}>
                  <Option value="All Routes">All Statuses</Option>
                  {Object.keys(STATUS_CFG).map((s) => (
                    <Option key={s} value={s}>
                      {s}
                    </Option>
                  ))}
                </Select>
                <Input
                  allowClear
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search route, driver, vehicle…"
                  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                  style={{ width: 280, borderRadius: 12 }}
                />
              </Space>
            </div>

            <div style={{ marginTop: 16 }}>
              <Table
                dataSource={routes}
                columns={routeColumns}
                rowKey="id"
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
                scroll={{ x: "max-content" }}
                rowClassName={(r) => (Number(r.id) === Number(selectedRouteId) ? "ant-table-row-selected" : "")}
                onRow={(r) => ({ onClick: () => setSelectedRouteId(r.id), style: { cursor: "pointer" } })}
                size="middle"
              />
            </div>
          </Card>

          <Row gutter={18}>
            <Col xs={24} lg={17}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 22,
                  boxShadow: "0 10px 30px rgba(15,23,42,0.07)",
                  border: "1px solid rgba(226,232,240,0.9)",
                  overflow: "hidden",
                }}
                bodyStyle={{ padding: 18 }}
                title={
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900, fontSize: 15, color: "#0f172a" }}>
                      Orders — {selectedRoute?.name || "select a route above"}
                    </div>
                    {selectedRoute ? <StatusBadge status={selectedRoute.status || "Planned"} /> : null}
                  </div>
                }
                extra={<RightOutlined style={{ color: "#94a3b8" }} />}
              >
                <Table
                  dataSource={routeOrders}
                  columns={orderColumns}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: "max-content" }}
                  locale={{ emptyText: <Empty description="Select a route to view its orders" /> }}
                  size="small"
                />
              </Card>
            </Col>

            <Col xs={24} lg={7}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 22,
                  boxShadow: "0 10px 30px rgba(15,23,42,0.07)",
                  border: "1px solid rgba(226,232,240,0.9)",
                  overflow: "hidden",
                }}
                bodyStyle={{ padding: 18 }}
                title={<span style={{ fontWeight: 900, fontSize: 15 }}>Route Actions</span>}
              >
                {selectedRoute && (
                  <div
                    style={{
                      background: "linear-gradient(180deg,#fbfdff,#f8fafc)",
                      borderRadius: 18,
                      padding: 16,
                      marginBottom: 14,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a" }}>{safe(selectedRoute.name)}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Route summary and quick actions</div>
                      </div>
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 14,
                          background: "linear-gradient(135deg,#d4af37,#f0c93a)",
                          color: "#1a2b49",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <TruckOutlined />
                      </div>
                    </div>

                    <Divider style={{ margin: "14px 0" }} />

                    <div style={{ display: "grid", gap: 10, fontSize: 12, color: "#475569" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <span>Scheduled Date</span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{formatDate(selectedRoute.scheduledDate)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <span>Driver</span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{safe(selectedRoute.driver, "No Driver")}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <span>Vehicle</span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{safe(selectedRoute.vehicle)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <span>Day</span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{safe(selectedRoute.day || selectedRoute?.slot?.day || "No Day")}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <span>Slot Time</span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>
                          {safe(selectedRoute.slotStartTime || selectedRoute?.slot?.startTime, "—")} - {safe(selectedRoute.slotEndTime || selectedRoute?.slot?.endTime, "—")}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { label: "Mark as Loading", action: "loading", bg: "#FAEEDA", color: "#854F0B" },
                    { label: "Dispatch Route", action: "dispatch", bg: "#E6F1FB", color: "#185FA5" },
                    { label: "Complete Route", action: "complete", bg: "#EAF3DE", color: "#3B6D11" },
                  ].map((a) => (
                    <button
                      key={a.action}
                      onClick={() => handleRouteAction(a.action)}
                      disabled={!selectedRoute || actionLoading}
                      style={{
                        background: a.bg,
                        color: a.color,
                        border: "none",
                        borderRadius: 14,
                        padding: "12px 14px",
                        cursor: selectedRoute && !actionLoading ? "pointer" : "not-allowed",
                        fontSize: 13,
                        fontWeight: 800,
                        textAlign: "left",
                        opacity: selectedRoute && !actionLoading ? 1 : 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                    >
                      <span>{a.label}</span>
                      <RightOutlined />
                    </button>
                  ))}

                  <Divider style={{ margin: "4px 0" }} />

                  <button
                    onClick={downloadCSV}
                    disabled={!routeDetail}
                    style={{
                      background: "linear-gradient(135deg,#EEEDFE,#F7F5FF)",
                      color: "#534AB7",
                      border: "none",
                      borderRadius: 14,
                      padding: "12px 14px",
                      cursor: routeDetail ? "pointer" : "not-allowed",
                      fontSize: 13,
                      fontWeight: 800,
                      textAlign: "left",
                      opacity: routeDetail ? 1 : 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <DownloadOutlined />
                      Download Route Sheet
                    </span>
                    <RightOutlined />
                  </button>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        <RouteModal
          visible={showCreate}
          mode="create"
          slots={slots}
          drivers={drivers}
          vehicles={vehicles}
          initialValues={null}
          onClose={() => setShowCreate(false)}
          onSave={createRoute}
        />

        <RouteModal
          visible={!!editRoute}
          mode="edit"
          slots={slots}
          drivers={drivers}
          vehicles={vehicles}
          initialValues={editRoute}
          onClose={() => setEditRoute(null)}
          onSave={updateRoute}
        />

        <AssignModal
          visible={showAssign}
          routes={routes}
          orders={unassignedOrders}
          selectedRouteId={selectedRouteId}
          onClose={() => setShowAssign(false)}
          onConfirm={assignOrders}
        />

        <SlotManagerModal
          visible={showSlotsManager}
          slots={slots}
          onClose={() => setShowSlotsManager(false)}
          onSave={saveSlot}
          onDelete={deleteSlot}
        />

        <ListManagerModal
          visible={showDriversManager}
          title={<span style={{ fontWeight: 900, fontSize: 16 }}>Manage Drivers</span>}
          itemLabel="Driver"
          items={drivers}
          onClose={() => setShowDriversManager(false)}
          onAdd={saveDriver}
          onUpdate={updateDriver}
          onDelete={deleteDriver}
        />

        <ListManagerModal
          visible={showVehiclesManager}
          title={<span style={{ fontWeight: 900, fontSize: 16 }}>Manage Vehicles</span>}
          itemLabel="Vehicle"
          items={vehicles}
          onClose={() => setShowVehiclesManager(false)}
          onAdd={saveVehicle}
          onUpdate={updateVehicle}
          onDelete={deleteVehicle}
        />
      </div>
    </Spin>
  );
}
